import fire from '@store/firebase';
import { v4 as uuidv4 } from 'uuid';
import 'firebase/storage';

const fireStorage = fire.storage();
// Increase retry windows to reduce 'max retry time exceeded' failures on slow networks
try {
  fireStorage.setMaxUploadRetryTime(3 * 60 * 1000); // 3 minutes
  fireStorage.setMaxOperationRetryTime(3 * 60 * 1000);
} catch {}
const firestore = fire.firestore();

// contentType에 맞는 확장자 매핑
function getExtensionFromContentType(contentType: string): string {
  const typeMap: Record<string, string> = {
    'image/jpeg': '.jpg',
    'image/jpg': '.jpg',
    'image/png': '.png',
    'image/gif': '.gif',
    'image/webp': '.webp',
    'image/svg+xml': '.svg',
    'image/bmp': '.bmp',
    'image/tiff': '.tiff',
  };
  return typeMap[contentType] || '.jpg';
}

export async function saveAndGetImgURL(
  directory: string,
  selectFile: File,
  noAttatch: boolean = false,
) {
  // 파일명이 없거나 확장자가 없는 경우 처리
  const originalName = selectFile.name || 'image';

  // 파일의 실제 contentType에서 확장자 추출
  const extensionFromType = getExtensionFromContentType(selectFile.type);

  // 원본 파일명에 확장자가 있으면 사용, 없으면 contentType 기반 확장자 사용
  const fileExtension = originalName.includes('.')
    ? originalName.substring(originalName.lastIndexOf('.'))
    : extensionFromType;

  const baseNameWithoutExt = originalName.includes('.')
    ? originalName.substring(0, originalName.lastIndexOf('.'))
    : originalName;

  const fileName = !noAttatch
    ? `${baseNameWithoutExt}_${uuidv4()}${fileExtension}`
    : `${baseNameWithoutExt}${fileExtension}`;

  const filePath = `${directory}/${fileName}`;
  const fileRef = fireStorage.ref(filePath);

  // 메타데이터 설정 (다운로드 시 원본 파일명 유지, UUID는 스토리지 경로에만 사용)
  const metadata = {
    contentType: selectFile.type || 'image/jpeg',
    contentDisposition: `inline; filename="${encodeURIComponent(originalName)}"`,
  };

  await fileRef.put(selectFile, metadata);
  return fileRef.getDownloadURL();
}

export async function saveAndGetImgUrlByUrl(
  directory: string,
  url: Blob | Uint8Array | ArrayBuffer,
  originalFileName?: string,
) {
  // 파일명 처리
  const timestamp = new Date().getTime();
  const fileName = originalFileName || `image_${timestamp}`;

  // Blob인 경우 contentType에서 확장자 추출
  const extensionFromType =
    url instanceof Blob ? getExtensionFromContentType(url.type) : '.jpg';

  const fileExtension = fileName.includes('.')
    ? fileName.substring(fileName.lastIndexOf('.'))
    : extensionFromType;

  const baseNameWithoutExt = fileName.includes('.')
    ? fileName.substring(0, fileName.lastIndexOf('.'))
    : fileName;

  const fullFileName = `${baseNameWithoutExt}_${uuidv4()}${fileExtension}`;
  const filePath = `${directory}/${fullFileName}`;
  const fileRef = fireStorage.ref(filePath);

  // 메타데이터 설정
  const metadata = {
    contentType: url instanceof Blob ? url.type || 'image/jpeg' : 'image/jpeg',
    contentDisposition: `inline; filename="${encodeURIComponent(
      fullFileName,
    )}"`,
  };

  await fileRef.put(url, metadata);

  return fileRef.getDownloadURL();
}

/**
 * URL에서 이미지를 가져와 새 경로에 복사하여 저장
 * 원본과 독립적인 새 이미지 URL을 반환
 */
export async function copyImageToNewPath(
  sourceURL: string,
  targetDirectory: string,
): Promise<string | null> {
  if (!sourceURL || !sourceURL.startsWith('http')) {
    return null;
  }

  try {
    // URL에서 이미지를 Blob으로 가져오기
    const response = await fetch(sourceURL);
    if (!response.ok) {
      console.warn('이미지 fetch 실패:', sourceURL);
      return null;
    }

    const blob = await response.blob();

    // 원본 파일명 추출 (URL에서)
    let originalFileName = 'copied_image';
    try {
      const urlObj = new URL(sourceURL);
      const pathParts = urlObj.pathname.split('/');
      const lastPart = pathParts[pathParts.length - 1];
      // Firebase Storage URL에서 파일명 추출 (인코딩된 경로에서)
      if (lastPart) {
        const decodedName = decodeURIComponent(lastPart);
        // 확장자 제외한 파일명만 추출
        const nameWithoutExt = decodedName.includes('.')
          ? decodedName.substring(0, decodedName.lastIndexOf('.'))
          : decodedName;
        if (nameWithoutExt) {
          originalFileName = nameWithoutExt;
        }
      }
    } catch {}

    // 새 경로에 저장
    const newURL = await saveAndGetImgUrlByUrl(
      targetDirectory,
      blob,
      originalFileName,
    );

    return newURL;
  } catch (error) {
    console.error('이미지 복사 실패:', error);
    return null;
  }
}

/**
 * 여러 이미지 URL을 새 경로에 복사
 * 원본 URL 배열과 같은 순서로 새 URL 배열 반환
 */
export async function copyImagesToNewPath(
  sourceURLs: string[],
  targetDirectory: string,
): Promise<string[]> {
  const results: string[] = [];

  for (const url of sourceURLs) {
    const newURL = await copyImageToNewPath(url, targetDirectory);
    if (newURL) {
      results.push(newURL);
    }
  }

  return results;
}

export async function deleteImgStorage(imgURL: string) {
  const ref = fireStorage.refFromURL(imgURL);
  try {
    await ref.getMetadata(); // 존재 확인
    await ref.delete(); // 존재하면 삭제
  } catch (err: any) {
    if (err.code === 'storage/object-not-found') {
      // 파일 없음 → 조용히 무시
      return;
    }
    throw err; // 그 외 에러는 던짐
  }
}

export function saveImgList(imgList) {
  imgList.map(async (list) => {
    const { selectFile } = list;

    // 파일명 처리
    const originalName = selectFile.name || 'image';

    // contentType 기반 확장자
    const extensionFromType = getExtensionFromContentType(selectFile.type);

    const fileExtension = originalName.includes('.')
      ? originalName.substring(originalName.lastIndexOf('.'))
      : extensionFromType;

    const baseNameWithoutExt = originalName.includes('.')
      ? originalName.substring(0, originalName.lastIndexOf('.'))
      : originalName;

    const fileName = `${baseNameWithoutExt}_${uuidv4()}${fileExtension}`;
    const filePath = `note/${fileName}`;
    const fileRef = fireStorage.ref(filePath);

    // 메타데이터 설정
    const metadata = {
      contentType: selectFile.type || 'image/jpeg',
      contentDisposition: `inline; filename="${encodeURIComponent(fileName)}"`,
    };

    await fileRef.put(selectFile, metadata);
  });
}

type updateImgAndGetURLProps = {
  selectedFile?: File | null;
  prevImgURL?: string | null;
  collectionPath: string;
  deletePrevURL?: boolean;
};

export async function updateImgAndGetURL({
  selectedFile,
  prevImgURL,
  collectionPath,
  deletePrevURL = true,
}: updateImgAndGetURLProps) {
  let imgURL: string = prevImgURL || '';

  if (selectedFile) {
    try {
      if (deletePrevURL && prevImgURL) deleteImgStorage(prevImgURL);
      imgURL = await saveAndGetImgURL(collectionPath, selectedFile);
    } catch (err) {
      alert(
        buildStorageErrorMessage(err, {
          phase: 'updateImgAndGetURL',
          collectionPath,
          prevImgURL,
          fileName: selectedFile?.name,
          fileSize: selectedFile?.size,
        }),
      );
    }
  }

  return imgURL;
}

type DeleteImgProps = {
  collectionPath: string;
  docId?: string;
  imgURL: string;
  key: string;
  prevOptions?: any;
};

type UpdateImgProps = {
  fileList: {
    selectedFile: File | null;
    prevImgURL: string | null;
  };
  key: string;
  collectionPath: string;
  directory?: string;
  docId?: string;
  prevOptions?: any;
};

export async function updateImg({
  fileList,
  key,
  collectionPath,
  directory,
  docId,
}: UpdateImgProps) {
  const { selectedFile, prevImgURL } = fileList;

  let imgURL: string | null = prevImgURL || '';

  if (selectedFile) {
    try {
      if (prevImgURL) deleteImgStorage(prevImgURL);
      imgURL = await saveAndGetImgURL(
        directory || collectionPath,
        selectedFile,
      );
    } catch (err) {
      alert(
        buildStorageErrorMessage(err, {
          phase: 'updateImg',
          collectionPath,
          directory,
          fileName: selectedFile?.name,
          fileSize: selectedFile?.size,
        }),
      );
    }
  }
  return firestore
    .collection(collectionPath)
    .doc(docId)
    .set({ [key]: imgURL }, { merge: true })
    .catch((err) => alert(err.message));
}

export async function updateOptionImg({
  fileList,
  key,
  collectionPath,
  directory,
  docId,
  prevOptions,
}: UpdateImgProps) {
  const { selectedFile, prevImgURL } = fileList;

  let imgURL: string | null = prevImgURL || '';

  if (selectedFile) {
    try {
      if (prevImgURL) deleteImgStorage(prevImgURL);
      imgURL = await saveAndGetImgURL(
        directory || collectionPath,
        selectedFile,
      );
    } catch (err) {
      alert(
        buildStorageErrorMessage(err, {
          phase: 'updateOptionImg',
          collectionPath,
          directory,
          fileName: selectedFile?.name,
          fileSize: selectedFile?.size,
        }),
      );
    }
  }
  return firestore
    .collection(collectionPath)
    .doc(docId)
    .set({ options: { ...prevOptions, [key]: imgURL } }, { merge: true })
    .catch((err) => alert(err.message));
}

export async function deleteOptionsImg({
  collectionPath,
  docId,
  imgURL,
  key,
  prevOptions,
}: DeleteImgProps) {
  const ref = firestore.collection(collectionPath);
  if (imgURL) deleteImgStorage(imgURL);

  return ref
    .doc(docId)
    .set({ options: { ...prevOptions, [key]: '' } }, { merge: true })
    .catch((err) => alert(err.message));
}

export async function deleteImg({
  collectionPath,
  docId,
  imgURL,
  key,
}: DeleteImgProps) {
  const ref = firestore.collection(collectionPath);
  if (imgURL) deleteImgStorage(imgURL);

  return ref
    .doc(docId)
    .set({ [key]: '' }, { merge: true })
    .catch((err) => alert(err.message));
}

// ---------------- Error helpers ----------------
type StorageErrContext = {
  operation?: string;
  phase?: string;
  path?: string;
  directory?: string;
  contentType?: string;
  fileName?: string;
  fileSize?: number;
  prevImgURL?: string | null;
  collectionPath?: string;
};

function isOnline() {
  try {
    return typeof navigator !== 'undefined' ? navigator.onLine : true;
  } catch {
    return true;
  }
}

function enrichStorageError(err: any, ctx: StorageErrContext) {
  const e: any = err || {};
  e.__ctx = ctx;
  return e;
}

function buildStorageErrorMessage(err: any, ctx: StorageErrContext) {
  const code = err?.code || '';
  const message = err?.message || '';
  const serverResponse = err?.serverResponse;
  const status = err?.status;
  const hintOffline = !isOnline() ? '\n- 네트워크가 오프라인 상태입니다.' : '';

  let reason = '';
  switch (code) {
    case 'storage/retry-limit-exceeded':
      reason = '재시도 한도를 초과했습니다 (네트워크 지연/중단 가능).';
      break;
    case 'storage/unauthorized':
      reason = '권한이 없습니다 (보안 규칙 확인 필요).';
      break;
    case 'storage/canceled':
      reason = '업로드가 취소되었습니다.';
      break;
    case 'storage/unknown':
      reason = '알 수 없는 오류가 발생했습니다.';
      break;
    case 'storage/quota-exceeded':
      reason = '스토리지 할당량을 초과했습니다.';
      break;
    case 'storage/invalid-checksum':
      reason = '전송 중 파일 무결성 오류(체크섬 불일치).';
      break;
    default:
      reason = code ? `오류 코드: ${code}` : '오류 코드 없음';
  }

  const ctxText = `
[진단]
- 단계: ${ctx.phase || ctx.operation || '-'}
- 경로: ${ctx.path || ctx.directory || '-'}
- 파일: ${ctx.fileName || '-'} (${
    typeof ctx.fileSize === 'number' ? ctx.fileSize : '-'
  } bytes)
- 타입: ${ctx.contentType || '-'}${hintOffline}`;

  const details = serverResponse
    ? `\n[서버 응답]\n${String(serverResponse).slice(0, 500)}`
    : status
    ? `\n[HTTP 상태] ${status}`
    : '';

  try {
    // eslint-disable-next-line no-console
    console.error('[StorageError]', { code, message, err, ctx });
  } catch {}

  return `업로드 중 오류가 발생했습니다.\n${reason}\n${ctxText}\n\n원본 메시지: ${
    message || '없음'
  }${details}`;
}

type UpdateSettingImgProps = {
  fileList: {
    selectedFile: File | null;
    prevImgURL: string | null;
  };
  parentKey: string;
  key: string;
  collectionPath: string;
  directory?: string;
  docId?: string;
};

export async function updateSettingImg({
  fileList,
  key,
  parentKey,
  collectionPath,
  directory,
  docId,
}: UpdateSettingImgProps) {
  const { selectedFile, prevImgURL } = fileList;

  let imgURL: string | null = prevImgURL || '';

  if (selectedFile) {
    try {
      if (prevImgURL) deleteImgStorage(prevImgURL);
      imgURL = await saveAndGetImgURL(
        directory || collectionPath,
        selectedFile,
      );
    } catch (err) {
      alert(
        buildStorageErrorMessage(err, {
          phase: 'updateSettingImg',
          collectionPath,
          directory,
          fileName: selectedFile?.name,
          fileSize: selectedFile?.size,
        }),
      );
    }
  }
  return firestore
    .collection(collectionPath)
    .doc(docId)
    .set({ [parentKey]: { [key]: imgURL } }, { merge: true })
    .catch((err) => alert(err.message));
}

type DeleteSettingImgProps = {
  collectionPath: string;
  docId?: string;
  imgURL: string;
  parentKey: string;
  key: string;
};

export async function deleteSettingImg({
  collectionPath,
  docId,
  imgURL,
  parentKey,
  key,
}: DeleteSettingImgProps) {
  await firestore
    .collection(collectionPath)
    .doc(docId)
    .set({ [parentKey]: { [key]: '' } }, { merge: true })
    .catch((err) => alert(err.message));

  if (imgURL) deleteImgStorage(imgURL);
}
